<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { useDirtyStep } from '@/composables/useDirtySnapshot';
import { useCurrentTenant } from '@/composables/schoolAdmin/useCurrentTenant';
import { useSchoolLevelsBySchoolType } from '@/composables/useSchoolLevelsBySchoolType';
import { useSchoolYears } from '@/composables/useSchoolYears';
import CustomButton from '@/core/elements/CustomButton.vue';
import EmptyPlaceholder from '@/core/elements/EmptyPlaceholder.vue';
import FormSelect from '@/core/forms/FormSelect.vue';
import ClassIdentityFields from '@/core/dialogs/create-class/ClassIdentityFields.vue';
import type { TEntityAppearance } from '@/core/entity-appearance/entityAppearance.types';
import { ISelectOption } from '@/interfaces';
import { assertNotNullOrUndefined } from '@/utils/assertion.utils';
import { getClassNamePrefix } from '@/utils/schoolClass.utils';
import { getSchoolYearName } from '@/utils/schoolYear.utils';
import { optionalNormalizedString, requiredNormalizedString } from '@/utils/zod.utils';
import { toTypedSchema } from '@vee-validate/zod';
import { ECreateClassMode } from 'common/enum/misc.enum';
import {
  CLASS_OR_GROUP_NAME_MAX_LENGTH,
  CLASS_OR_GROUP_NICKNAME_MAX_LENGTH,
} from 'common/constants/school.class.constants';
import { useContainerTranslations } from '@/schoolClasses/useContainerTranslations';
import { useForm } from 'vee-validate';
import { computed, ref, toRefs, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { number, object } from 'zod';

const { t } = useI18n();
const { currentAndUpcomingSchoolYears } = useSchoolYears();
const { push } = useRouter();
const { currentTenant } = useCurrentTenant();
const { schoolLevelOptions, schoolLevels, useRawSchoolLevelLabels } = useSchoolLevelsBySchoolType();
const { isGroup, tContainer } = useContainerTranslations();

const emit = defineEmits<{
  continue: [value: Components.Schemas.CreateSchoolClassDto];
  'update:isValid': [value: boolean];
}>();

const props = defineProps<{
  schoolClass: Components.Schemas.CreateSchoolClassDto | null;
  mode: ECreateClassMode;
}>();
const { schoolClass, mode } = toRefs(props);
const appearance = defineModel<TEntityAppearance>('appearance', { required: true });
const identityFields = useTemplateRef('identityFields');

const validationSchema = computed(() =>
  toTypedSchema(
    object({
      schoolLevel: number().min(0),
      name: requiredNormalizedString(t('errorMessages.noName')).refine(
        (value) => value.length <= CLASS_OR_GROUP_NAME_MAX_LENGTH,
        t('errorMessages.maxLength', { max: CLASS_OR_GROUP_NAME_MAX_LENGTH }),
      ),
      nickname: optionalNormalizedString().refine(
        (value) => value == null || value.length <= CLASS_OR_GROUP_NICKNAME_MAX_LENGTH,
        t('errorMessages.maxLength', { max: CLASS_OR_GROUP_NICKNAME_MAX_LENGTH }),
      ),
      schoolYearId: number(),
    }),
  ),
);

const { handleSubmit, isSubmitting, resetForm, meta, setFieldValue, values } = useForm({
  validationSchema,
});

useDirtyStep(() => ({
  name: values.name ?? '',
  nickname: values.nickname ?? '',
  schoolLevel: values.schoolLevel ?? null,
  schoolYearId: values.schoolYearId ?? null,
}));

const getDefaultSchoolLevel = (): number =>
  schoolLevels.value.find((level) => level !== 0) ?? schoolLevels.value.at(0) ?? 1;
const selectedSchoolLevel = ref<number>(getDefaultSchoolLevel());

const selectedSchoolYearId = ref<number | null>(null);
const schoolYearOptions = computed<ISelectOption<number>[]>(() =>
  currentAndUpcomingSchoolYears.value.map((schoolYear) => ({
    label: getSchoolYearName(schoolYear, t),
    value: schoolYear.id,
  })),
);
const defaultAbbreviation = (level: number): string => {
  if (isGroup.value) return '';
  return `${getClassNamePrefix(level, schoolLevels.value, useRawSchoolLevelLabels.value)}a`;
};

const getInitialSchoolYearId = (): number | undefined => {
  const initialSchoolYearId = schoolClass.value?.schoolYearId;
  if (initialSchoolYearId == null) return schoolYearOptions.value[0]?.value;
  if (schoolYearOptions.value.some((schoolYearOption) => schoolYearOption.value === initialSchoolYearId)) {
    return initialSchoolYearId;
  }
  return schoolYearOptions.value[0]?.value;
};

const onSubmit = handleSubmit((formValues) => {
  identityFields.value?.closeAppearance();
  emit('continue', {
    name: formValues.name,
    nickname: formValues.nickname ?? '',
    schoolLevel: formValues.schoolLevel,
    schoolYearId: formValues.schoolYearId,
    isActive: true,
    isTemplate: false,
  });
});

const goToSchoolYears = async (): Promise<void> => {
  assertNotNullOrUndefined(currentTenant.value?.id);
  await push({ name: '//school/[schoolId]/settings/schoolYears/', params: { schoolId: currentTenant.value.id } });
};

const setSchoolYearId = (schoolYearId: number): void => {
  selectedSchoolYearId.value = schoolYearId;
  setFieldValue('schoolYearId', schoolYearId, true);
};

watch(
  [schoolClass, schoolLevels, schoolYearOptions],
  () => {
    const defaultValues = {
      schoolLevel: getDefaultSchoolLevel(),
      nickname: undefined,
      schoolYearId: undefined,
    };
    selectedSchoolLevel.value = schoolClass.value?.schoolLevel ?? defaultValues.schoolLevel;
    selectedSchoolYearId.value = getInitialSchoolYearId() ?? null;
    resetForm({
      values: {
        schoolLevel: selectedSchoolLevel.value,
        name: schoolClass.value?.name ?? defaultAbbreviation(selectedSchoolLevel.value),
        nickname: schoolClass.value?.nickname ?? defaultValues.nickname,
        schoolYearId: selectedSchoolYearId.value ?? defaultValues.schoolYearId,
      },
    });
  },
  { immediate: true },
);

watch(selectedSchoolLevel, (level, previousLevel) => {
  if (values.name === defaultAbbreviation(previousLevel)) {
    setFieldValue('name', defaultAbbreviation(level));
  }
});

watch(
  [meta, isSubmitting],
  () => {
    emit('update:isValid', meta.value.valid && !isSubmitting.value);
  },
  { immediate: true },
);

defineExpose({
  onSubmit,
  setSchoolYearId,
});
</script>

<template>
  <form class="flex flex-col px-lg md:px-content" @submit="onSubmit">
    <p class="text-subtitle mb-md">{{ tContainer('generalDataDescription') }}</p>

    <div class="flex flex-col gap-lg bg-surface-secondary p-lg rounded-group">
      <FormSelect
        v-if="schoolYearOptions.length"
        name="schoolYearId"
        :value="selectedSchoolYearId"
        :options="schoolYearOptions"
        :label="
          mode === ECreateClassMode.NEW_CLASS || mode === ECreateClassMode.MINIMAL
            ? $t('classes.year')
            : $t('classes.newYear')
        "
        @update:value="selectedSchoolYearId = $event"
      />
      <div v-else class="flex flex-col gap-md">
        <EmptyPlaceholder :data="schoolYearOptions" :message="$t('schoolYears.noSchoolYears')" />
        <CustomButton theme="link" class="self-end" @click="goToSchoolYears">
          {{ $t('general.schoolYears') }}
        </CustomButton>
      </div>
      <FormSelect
        name="schoolLevel"
        :value="selectedSchoolLevel"
        :options="schoolLevelOptions"
        :label="
          mode === ECreateClassMode.NEW_CLASS || mode === ECreateClassMode.MINIMAL
            ? $t('onboarding.class.schoolLevel')
            : $t('classes.newSchoolLevel')
        "
        @update:value="selectedSchoolLevel = $event"
      />
      <div class="border-t border-divider"></div>
      <ClassIdentityFields
        ref="identityFields"
        v-model:appearance="appearance"
        name-field="nickname"
        abbreviation-field="name"
        :abbreviation="values.name ?? ''"
        :name-optional="!isGroup"
      />
    </div>
  </form>
</template>
